import React, { useState } from "react";
import { Card, Input, Space, Button, Row, Col, message, Spin, Typography } from "antd";
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { api } from "../../config/api";
import { Link } from "react-router-dom";
import { destroyAuthToken } from "../../helpers/account";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const { Title, Paragraph } = Typography;

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [emailConfirmed, setEmailConfirmed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [messageApi, contextHolder] = message.useMessage();

  const handleLogin = () => {
    const apiUrl = `${api.host}/accounts/login`;
    setLoading(true);
    const requestBody = { email, password, code };

    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (response.status === 200) {
          setLoading(false);
          response
            .json()
            .then((data) => {
              if (data.authToken) {
                messageApi.open({
                  type: "success",
                  content: t('login.loginSuccess'),
                });
                sessionStorage.setItem("authToken", data.authToken);
                sessionStorage.setItem("uuid", data.uuid);
                setTimeout(() => {
                  let lastVisitedPath = sessionStorage.getItem('lastVisitedPath');
                  lastVisitedPath = lastVisitedPath == '/' ? "/transactions" : lastVisitedPath;
                  window.location.href = (lastVisitedPath || "/transactions");
                }, 2000);
              } else {
                setEmailConfirmed(false);
                messageApi.open({
                  type: "error",
                  content: t('login.emailNotConfirmed'),
                });
              }
            })
            .catch((error) => {
              messageApi.open({
                type: "error",
                content: t('login.loginError'),
              });
            });
        } else {
          setLoading(false);
          messageApi.open({
            type: "warning",
            content: t('login.invalidCredentials'),
          });
          destroyAuthToken();
        }
      })
      .catch((error) => {
        setLoading(false);
        messageApi.open({
          type: "warning",
          content: t('login.invalidCredentials'),
        });
        destroyAuthToken();
      });
  };

  return (
    <>
      <Helmet>
        <title>{t('login.title')}</title>
      </Helmet>
      
      {/* Hero Section */}
      <section style={{ 
        minHeight: "100vh", 
        display: "flex", 
        alignItems: "center", 
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "40px 20px"
      }}>
        {contextHolder}
        <div style={{ maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={12}>
              <div style={{ color: "white", textAlign: { xs: "center", lg: "left" } }}>
                <Title level={1} style={{ 
                  color: "white", 
                  fontSize: "clamp(2rem, 5vw, 3.5rem)", 
                  marginBottom: "24px" 
                }}>
                  {t('login.welcomeBack')}
                </Title>
                <Paragraph style={{ 
                  fontSize: "clamp(1rem, 2.5vw, 1.25rem)", 
                  color: "rgba(255,255,255,0.9)",
                  marginBottom: "40px"
                }}>
                  {t('login.subtitle')}
                </Paragraph>
                <div style={{ fontSize: "clamp(60px, 12vw, 120px)", lineHeight: "1", padding: "20px 0" }}>
                  👋
                </div>
              </div>
            </Col>
            
            <Col xs={24} lg={12}>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Spin spinning={loading}>
                  <Card
                    style={{
                      width: "100%",
                      maxWidth: "400px",
                      border: "none",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                      borderRadius: "5px"
                    }}
                  >
                    <Space direction="vertical" size="large" style={{ width: "100%" }}>
                      <div style={{ textAlign: "center", marginBottom: "20px" }}>
                        <Title level={3} style={{ margin: 0, color: "#2c3e50" }}>
                          {t('login.loginTitle')}
                        </Title>
                        <Paragraph style={{ color: "#6c757d", margin: "8px 0 0 0" }}>
                          {t('login.enterCredentials')}
                        </Paragraph>
                      </div>

                      <Input
                        size="large"
                        autoComplete="off"
                        placeholder={t('register.emailPlaceholder')}
                        prefix={<UserOutlined style={{ color: "#6c757d" }} />}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{ borderRadius: "8px" }}
                      />

                      <Input.Password
                        size="large"
                        autoComplete="off"
                        placeholder={t('register.passwordPlaceholder')}
                        prefix={<LockOutlined style={{ color: "#6c757d" }} />}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ borderRadius: "8px" }}
                      />

                      {emailConfirmed === false && (
                        <>
                          <div style={{ 
                            background: "#f8f9fa", 
                            padding: "16px", 
                            borderRadius: "8px",
                            border: "1px solid #e9ecef"
                          }}>
                            <Paragraph style={{ margin: 0, fontSize: "14px", color: "#6c757d" }}>
                              {t('register.verificationInstructions')}
                            </Paragraph>
                          </div>
                          <Input
                            size="large"
                            autoComplete="off"
                            placeholder={t('register.verificationCodePlaceholder')}
                            prefix={<SafetyCertificateOutlined style={{ color: "#6c757d" }} />}
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            style={{ borderRadius: "8px" }}
                          />
                        </>
                      )}

                      <Button 
                        type="primary" 
                        size="large" 
                        onClick={handleLogin}
                        style={{ 
                          width: "100%", 
                          height: "56px",
                          borderRadius: "5px",
                          fontSize: "18px",
                          fontWeight: "600"
                        }}
                      >
                        {t('login.loginButton')}
                      </Button>

                      <div style={{ textAlign: "center", paddingTop: "16px" }}>
                        <Paragraph style={{ margin: 0, fontSize: "14px", color: "#6c757d" }}>
                          <Link to="/account/password-reset" style={{ color: "#667eea" }}>
                            {t('login.forgotPassword')}
                          </Link>
                        </Paragraph>
                        <Paragraph style={{ margin: "8px 0 0 0", fontSize: "14px", color: "#6c757d" }}>
                          {t('login.noAccount')}{" "}
                          <Link to="/account/register" style={{ color: "#667eea", fontWeight: "500" }}>
                            {t('login.createFreeAccount')}
                          </Link>
                        </Paragraph>
                      </div>
                    </Space>
                  </Card>
                </Spin>
              </div>
            </Col>
          </Row>
        </div>
      </section>
    </>
  );
};

export default LoginPage;